export type PublicEventType =
  | 'guest_hunt'
  | 'banquet'
  | 'dinner'
  | 'golf_tournament'
  | 'clay_shoot'
  | 'association_meeting'
  | 'fundraiser_auction'
  | 'kayak_trip'
  | 'retreat'
  | 'training_day'
  | 'fitness'
  | 'other'

export type EventQuestionType = 'text' | 'yes_no' | 'select'

export type EventQuestion = {
  id: string
  label: string
  type: EventQuestionType
  required: boolean
  options?: string[]
}

export type EventPricingMode = 'per_guest' | 'per_guest_day' | 'flat' | 'per_day'

export type ManagedEventStatus = 'scheduled' | 'cancelled'

export type EventScope = 'upcoming' | 'past' | 'draft' | 'cancelled' | 'all'

export type RegistrationStatus =
  | 'confirmed'
  | 'pending_documents'
  | 'waitlisted'
  | 'cancelled'
  | 'completed'
  | 'no_show'

export type ManagedRegistrationStatus = Exclude<
  RegistrationStatus,
  'waitlisted' | 'pending_documents'
>

export type ManagedEvent = {
  id: string
  orgId: string
  title: string
  description?: string
  location?: string
  eventType: PublicEventType
  startsAt: string
  endsAt?: string
  capacity?: number
  price?: number
  pricingMode: EventPricingMode
  waitlistEnabled: boolean
  requiresApproval: boolean
  requirePhone: boolean
  allowGuests: boolean
  questions: EventQuestion[]
  isPublished: boolean
  status: ManagedEventStatus
  heroImageUrl?: string
  cancelledAt?: string
  cancellationReason?: string
  createdAt: string
  updatedAt: string
  confirmedSeats: number
  confirmedCount: number
  waitlistedCount: number
  paidCount: number
  pendingDocumentsCount: number
}

export type EventRegistration = {
  id: string
  eventId: string
  orgId: string
  name: string
  email?: string
  phone?: string
  partySize: number
  message?: string
  answers: Record<string, string>
  status: RegistrationStatus
  confirmedAt?: string
  cancelledAt?: string
  cancellationReason?: string
  paymentRecordedAt?: string
  createdAt: string
}

export const EVENT_TYPE_LABELS: Record<PublicEventType, string> = {
  guest_hunt: 'Guest hunt',
  banquet: 'Banquet',
  dinner: 'Dinner',
  golf_tournament: 'Golf tournament',
  clay_shoot: 'Clay shoot',
  association_meeting: 'Association meeting',
  fundraiser_auction: 'Fundraiser / auction',
  kayak_trip: 'Kayak trip',
  retreat: 'Retreat',
  training_day: 'Training day',
  fitness: 'Fitness',
  other: 'Other',
}

export const EVENT_QUESTION_TYPE_LABELS: Record<EventQuestionType, string> = {
  text: 'Text answer',
  yes_no: 'Yes / no',
  select: 'Multiple choice',
}

export function hasUnansweredRequired(
  questions: EventQuestion[],
  answers: Record<string, string>,
): boolean {
  return questions.some(
    (question) => question.required && !(answers[question.id] ?? '').trim(),
  )
}

export const PRICING_MODE_LABELS: Record<EventPricingMode, string> = {
  per_guest: 'per guest',
  per_guest_day: 'per guest, per day',
  flat: 'flat rate',
  per_day: 'per day',
}

export const REGISTRATION_STATUS_LABELS: Record<RegistrationStatus, string> = {
  confirmed: 'Confirmed',
  pending_documents: 'Awaiting documents',
  waitlisted: 'Waitlisted',
  cancelled: 'Cancelled',
  completed: 'Completed',
  no_show: 'No-show',
}

export type EventDocumentKind = 'sign' | 'upload'

export type LinkedEventDocument = {
  templateId: string
  name: string
  kind: EventDocumentKind
  status: string
}

export type EventDocumentSummary = {
  templateId: string
  name: string
  kind: EventDocumentKind
}

export type RegistrationDocumentState =
  | 'signed'
  | 'uploaded'
  | 'sent'
  | 'viewed'
  | 'declined'
  | 'expired'
  | 'voided'

const SETTLED_DOCUMENT_STATES: RegistrationDocumentState[] = ['signed', 'uploaded']

const DEAD_DOCUMENT_STATES: RegistrationDocumentState[] = [
  'declined',
  'expired',
  'voided',
]

export function isDocumentSettled(
  status: RegistrationDocumentState | undefined,
): boolean {
  return status !== undefined && SETTLED_DOCUMENT_STATES.includes(status)
}

export function isDocumentDead(
  status: RegistrationDocumentState | undefined,
): boolean {
  return status !== undefined && DEAD_DOCUMENT_STATES.includes(status)
}

export type RegistrationDocumentStatus = {
  registrationId: string
  templateId: string
  status: RegistrationDocumentState
  signatureRequestId?: string
}

export type OrgDocumentTemplate = {
  id: string
  name: string
  kind: EventDocumentKind
}
